import Rioglyph from '@rio-cloud/rio-uikit/Rioglyph';
import classNames from 'classnames';
import { ServiceProviderColumn } from './ServiceProviderTableColumns';
import { ReactNode } from 'react';
import { InterfaceClient, ServiceProvider, ServiceProviderAction, Tenant } from './serviceProvider.types';
import { formatDate } from 'date-fns';
import { NoValue } from '../sharedComponents/NoValue';
import { ServiceProviderActions } from './ServiceProviderActions';
import { FormattedMessage } from 'react-intl';
import { StatusLabel } from '../sharedComponents/StatusLabel';
import { CompoundAccessSelect } from './CompoundAccessSelect';
import { ColumnDefinition } from '../sharedComponents/table/SortedTableHeader';

const TenantsList = (props: { tenants: Tenant[] }) => {
    return (
        <>
            {props.tenants.length === 0 ? (
                <NoValue />
            ) : (
                props.tenants.map((tenant) => (
                    <div key={tenant}>
                        <span>{tenant}</span>
                    </div>
                ))
            )}
        </>
    );
};

const InterfaceClientCell = ({ interfaceClient }: { interfaceClient: InterfaceClient | undefined }) => {
    if (!interfaceClient) {
        return <NoValue />;
    }

    const { clientId, enabled } = interfaceClient;

    return (
        <span className="display-flex align-items-center gap-5">
            <Rioglyph
                icon="rioglyph-key"
                iconClassName={enabled ? 'text-color-success' : 'text-color-danger'}
                size="18"
                disabledInverse={!enabled}
            />
            <span className={classNames('word-break-all', { 'text-color-gray': !enabled })}>{clientId}</span>
        </span>
    );
};

type ServiceProvidersTableRowProps = {
    serviceProvider: ServiceProvider;
    allowedActions: ServiceProviderAction[];
    columnDefinitions: ColumnDefinition<ServiceProviderColumn>[];
};

export const ServiceProvidersTableRow = (props: ServiceProvidersTableRowProps) => {
    const renderColumnCell = (column: ServiceProviderColumn, rowData: ServiceProvider): string | ReactNode => {
        switch (column) {
            case ServiceProviderColumn.REGISTRATION_DATE:
                return formatDate(rowData.registrationDate, 'dd.MM.yyyy');
            case ServiceProviderColumn.ACTIVATION_DATE:
                return rowData.activationDate ? formatDate(rowData.activationDate, 'dd.MM.yyyy') : <NoValue />;
            case ServiceProviderColumn.API_ACCESS:
                return rowData.apiAccessRequested ? (
                    <FormattedMessage id="outboundOrderBook.common.yes" />
                ) : (
                    <FormattedMessage id="outboundOrderBook.common.no" />
                );
            case ServiceProviderColumn.ACTIVATION_STATUS:
                return <StatusLabel status={rowData.activationStatus} />;
            case ServiceProviderColumn.SERVICE_PROVIDER_ID:
                return rowData.serviceProviderId;
            case ServiceProviderColumn.SERVICE_PROVIDER_NAME:
                return rowData.serviceProviderName;
            case ServiceProviderColumn.DUNS:
                return rowData.duns;
            case ServiceProviderColumn.ACCOUNT_ID:
                return rowData.accountId;
            case ServiceProviderColumn.TENANTS:
                return <TenantsList tenants={rowData.tenants} />;
            case ServiceProviderColumn.CLIENT_ID:
                return <InterfaceClientCell interfaceClient={rowData.interfaceClient} />;
            case ServiceProviderColumn.COMPOUND_ACCESS:
                return <CompoundAccessSelect key={rowData.compoundAccess} serviceProvider={rowData} />;
            case ServiceProviderColumn.ACTION:
                return <ServiceProviderActions serviceProvider={rowData} allowedActions={props.allowedActions} />;
        }
    };

    return (
        <tr key={props.serviceProvider.accountId}>
            {props.columnDefinitions.map((def) => (
                <td
                    key={def.column}
                    data-field={def.column}
                    className={def.column === ServiceProviderColumn.COMPOUND_ACCESS ? 'overflow-visible' : undefined}
                >
                    <span>{renderColumnCell(def.column, props.serviceProvider)}</span>
                </td>
            ))}
        </tr>
    );
};
